import { convertKeysToCamelCase } from '@/js/utils/bff'
import i18n from '@/lang/i18n'

// 內用桌號列表儲存鍵
export const ORDER_FOOD_PRINT_TABLE_LIST = 'orderFoodPrintTableQrCodeTableList'

// 點數類型記憶值儲存鍵，內容為 { [社區ID]: 點數類型 }
export const ORDER_FOOD_POINT_TYPE_BY_COMMUNITY = 'orderFoodPointTypeByCommunity'

/**
 * 點數類型 Enum
 * @enum {number}
 */
export const PointType = Object.freeze({
  Normal: 1, // 一般點數
  Cash: 2 // 現金點數
})

/**
 * 取得目前登入社區的 ID
 * @returns {string} 取不到時回傳空字串
 */
const getCurrentCommunityId = () => {
  try {
    const comId = JSON.parse(localStorage.getItem('loginUserInfo'))?.ComId
    return comId === undefined || comId === null ? '' : String(comId)
  } catch (error) {
    return ''
  }
}

/**
 * 讀取各社區的點數類型記憶值
 * @returns {Object<string, number>} 內容損毀或不存在時回傳空物件
 */
const getRememberedPointTypeMap = () => {
  try {
    const map = JSON.parse(localStorage.getItem(ORDER_FOOD_POINT_TYPE_BY_COMMUNITY))
    return map && typeof map === 'object' && !Array.isArray(map) ? map : {}
  } catch (error) {
    return {}
  }
}

/**
 * 取得目前社區的點數類型記憶值
 * @returns {number|null} 無記憶值時回傳 null
 */
export const getRememberedPointType = () => {
  const comId = getCurrentCommunityId()
  if (!comId) return null
  const pointType = getRememberedPointTypeMap()[comId]
  return pointType === undefined ? null : pointType
}

/**
 * 寫入目前社區的點數類型記憶值（僅供人員手動切換時呼叫）
 * @param {number} pointType 點數類型
 */
export const setRememberedPointType = (pointType) => {
  const comId = getCurrentCommunityId()
  if (!comId) return
  const map = { ...getRememberedPointTypeMap(), [comId]: pointType }
  localStorage.setItem(ORDER_FOOD_POINT_TYPE_BY_COMMUNITY, JSON.stringify(map))
}

/**
 * 判斷是否為可用點數類型：
 * 至少存在一個「分類已啟用、品項已啟用、該點數類型價格已啟用」的品項
 * @param {array} menuList 餐點類別列表
 * @param {number} pointType 點數類型
 * @returns {boolean}
 */
export const isPointTypeAvailable = (menuList, pointType) => {
  return (menuList || []).some((menu) => {
    if (menu.isCheck !== 1) return false
    return (menu.children || []).some((item) => item.isCheck && item.prices?.[pointType - 1]?.isCheck)
  })
}

/**
 * 點數類型自動導向：決定進入點餐時預選的點數類型
 * 優先序：記憶值（須為社區提供且可用的點數類型）→ 僅一種可用選該種 → 其餘選一般點數
 * @param {object} params
 * @param {array} params.menuList 餐點類別列表
 * @param {array} params.pointTypeList 社區提供的點數類型列表（項目含 value）
 * @param {number|null} params.rememberedPointType 點數類型記憶值
 * @returns {number} 預選的點數類型
 */
export const resolveDefaultPointType = ({ menuList, pointTypeList, rememberedPointType }) => {
  const availablePointTypes = (pointTypeList || [])
    .map(point => point.value)
    .filter(value => isPointTypeAvailable(menuList, value))
  if (availablePointTypes.includes(rememberedPointType)) return rememberedPointType
  if (availablePointTypes.length === 1) return availablePointTypes[0]
  return PointType.Normal
}

/**
 * 已取消訂單狀態 Enum
 * @enum {number}
 */
export const CanceledOrderState = Object.freeze({
  OrderRejected: 10, // 接單拒絕
  MealVoided: 12, // 餐點作廢
  PickupCanceled: 13, // 取餐取消
  UserCanceled: 14 // 住戶取消
})

/**
 * 判斷訂單 state 是否為取消類狀態
 * @param {number} state
 * @returns {boolean}
 */
export const isCanceledState = (state) => Object.values(CanceledOrderState).includes(state)

/**
 * 用餐方式 Enum
 * @enum {number}
 */
export const DiningOption = Object.freeze({
  DineIn: 1, // 內用
  Takeout: 2, // 現場外帶
  Reservation: 3 // 預約
})

/**
 * 取得顯示規格
 * @param {array} specList 規格列表
 * @param {string} separator 分隔符號
 * @param {function} filterFunc 過濾規格選項
 */
export const getDisplaySpec = (specList, separator = ', ', filterFunc = (opt) => opt.isOption === 1) => {
  let displaySpec = ''
  const selectedSpecOptions = specList.flatMap(spec => {
    return spec.children.filter(filterFunc)
  })
  if (selectedSpecOptions.length > 0) {
    displaySpec += selectedSpecOptions.map(opt => opt.name).join(separator)
  }
  return displaySpec
}

/**
 * 用餐方式選項文字
 * @param {number} diningOption 1 內用, 2 外帶, 3 預約
 * @returns {string}
 */
export const getDiningOptionLabel = (diningOption) => {
  if (diningOption === DiningOption.DineIn) return i18n.t('OrderFoodManagement.DiningOptionDineIn')
  if (diningOption === DiningOption.Takeout) return i18n.t('OrderFoodManagement.DiningOptionTakeout')
  if (diningOption === DiningOption.Reservation) return i18n.t('OrderFoodManagement.DiningOptionReservation')
  return '-'
}

/**
 * 用餐方式資訊文字
 * @param {Object} dining 用餐資料
 * @param {number} dining.diningOption 1 內用, 2 現場外帶, 3 預約
 * @param {string} dining.tableNo 桌號
 * @returns {string}
 */
export const getDiningInfoText = (dining) => {
  if (!dining) return '-'
  const { diningOption, tableNo } = dining
  if (diningOption === DiningOption.DineIn) return tableNo || '-'
  if (diningOption === DiningOption.Takeout) return i18n.t('OrderFoodManagement.DiningInfoTakeout')
  if (diningOption === DiningOption.Reservation) return i18n.t('OrderFoodManagement.DiningInfoTakeout')
  return '-'
}

/**
 * 用餐方式合併字串（給列印或你要的單一欄位顯示）
 * 內用 => 內用 / 桌號
 * 外帶 => 外帶
 * 預約 => 預約自取 YYYY/MM/DD HH:ss
 * @param {Object} dining 用餐資料
 * @param {number} dining.diningOption 1 內用, 2 現場外帶, 3 預約
 * @param {string} dining.tableNo 桌號
 * @param {string} dining.reservationAtUtc 預約時間
 * @returns {string}
 */
export const getDiningDisplayText = (dining) => {
  if (!dining) return '-'
  const { diningOption, tableNo } = dining
  const optionLabel = getDiningOptionLabel(diningOption)
  if (diningOption === DiningOption.DineIn) {
    return `${optionLabel} / ${tableNo || '-'}`
  } else if (diningOption === DiningOption.Takeout) {
    return `${optionLabel}`
  } else if (diningOption === DiningOption.Reservation) {
    const timeStr = dining.reservationAtUtc
      ? `${dayjs(dining.reservationAtUtc).format('YYYY-MM-DD HH:mm')}`
      : ''
    return `${optionLabel}${i18n.t('OrderFoodManagement.DiningInfoTakeout')}${timeStr ? ` / ${timeStr}` : ''}`
  }
  return optionLabel
}

/**
 * 取得社區戶別點數資訊
 * @param {string} tabletId 戶別ID
 * @returns {Promise<{status: number, data: object}>} 回傳包含 status 和 data 的物件
 */
export const getCommunityTabletPointsData = async (tabletId) => {
  // lazy import: 避免與 router / createAxiosInstance 形成循環依賴
  const { apiOrderFood } = await import('@/api/OrderFood')
  const response = await apiOrderFood.getCommunityTabletPoints(tabletId)
  const { status, data } = convertKeysToCamelCase(response.data)
  return { status, data }
}

/**
 * 取值的最後四碼
 * @param {string|number|null|undefined} value 需要顯示的ID
 * @param {string} placeholder 當值不存在時顯示
 * @returns {string}
 */
export const getLast4Digits = (value, placeholder = '-') => {
  if (value === undefined || value === null) return placeholder
  const str = String(value).trim()
  if (!str) return placeholder
  const digitsOnly = str.replace(/\D/g, '')
  if (!digitsOnly) return placeholder
  if (digitsOnly.length <= 4) return digitsOnly
  return digitsOnly.slice(-4)
}

/**
 * 格式化訂單顯示代碼：月日（MMDD）＋訂單編號尾部四碼
 * @param {string|number|null|undefined} comOrderId 訂單編號
 * @param {string|Date|null|undefined} createdAtUtc 訂單建立時間（UTC）
 * @param {string} placeholder 當值不存在時顯示
 * @returns {string} 例如 "04161234"
 */
export const formatOrderDisplayCode = (comOrderId, createdAtUtc, placeholder = '-') => {
  const last4 = getLast4Digits(comOrderId, null)
  if (!last4) return placeholder
  if (!createdAtUtc) return last4
  const d = new Date(createdAtUtc)
  if (isNaN(d.getTime())) return last4
  const pad = (n) => String(n).padStart(2, '0')
  const mmdd = `${pad(d.getMonth() + 1)}${pad(d.getDate())}`
  return `${mmdd}${last4}`
}
